import { Tooltip } from '@material-ui/core';
import { Chip } from 'components/Modal/AddAttachment/components/Chip';
import {
  AssemblyModalities,
  AssemblyStatus,
  LegacyStatus,
} from 'helpers/enums/Assembleia';
import { DateFormats } from 'helpers/enums/DateTypes';
import {
  getFormatedDateTime,
  isDateLessThanToday,
} from 'helpers/utils/DateUtils';
import { clsx } from 'helpers/utils/HTMLUtils';
import { formatCEP } from 'helpers/utils/StringUtils';
import { AssociadoRoutePath } from 'library/Routes';
import { StateCode } from 'library/states';
import translation from 'locales/translation';
import {
  AnimatedLink,
  ChevronArrowRightIcon,
  Collapse,
  GlobeIcon,
  LocalIcon,
} from 'plataforma-fundacao-componentes';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styles from './styles.module.scss';

interface IProps {
  eventId: string;
  coopName: string;
  eventType: string;
  title: string;
  eventDate: DateFormats.ISODateTwoDots;
  eventEndDate: DateFormats.ISODateTwoDots;
  eventDatePresential: DateFormats.ISODateTwoDots | null;
  eventEndDatePresential: DateFormats.ISODateTwoDots | null;
  situation: LegacyStatus;
  onlineSituation: AssemblyStatus;
  cep: string | null;
  address: string | null;
  neighborhood: string | null;
  city: string | null;
  state: StateCode | null;
  modality: AssemblyModalities;
}

export function EventBigCard({
  eventId,
  eventType,
  title,
  coopName,
  eventDate,
  eventEndDate,
  eventDatePresential,
  eventEndDatePresential,
  modality,
  situation,
  onlineSituation,
  cep,
  address,
  neighborhood,
  city,
  state,
}: IProps) {
  const [openCollapse, setOpenCollapse] = useState<boolean>(false);
  const history = useHistory();

  const getStatusClassNamePresential = (status: LegacyStatus) => {
    switch (status) {
      case 1:
        return styles.AVAILABLE_OR_BLOCKED;
      case 2:
        return styles.FINISHED;
      default:
        return styles.DEFAULT;
    }
  };

  const getStatusClassNameOnline = (status: AssemblyStatus) => {
    switch (status) {
      case 1:
        return styles.PRE;
      case 2:
        return styles.LIVE;
      case 4:
        return styles.ENCERRADO;
      case 5:
        return styles.ENCERRADO_SEM_QUORUM;
      default:
        return styles.DEFAULT;
    }
  };

  const handleClick = useCallback(() => {
    history.push(`${AssociadoRoutePath}/assembleia/${eventId}/`);
  }, [eventId, history]);

  const votingBy = useMemo(
    () => (
      <Fragment>
        {eventType === 'Reunião'
          ? 'Término em: '
          : situation === LegacyStatus.FINISHED ||
            [
              AssemblyStatus.ENCERRADO,
              AssemblyStatus.ENCERRADO_SEM_QUORUM,
            ].includes(onlineSituation)
          ? `Terminou em: `
          : ` Término em: `}
        {eventEndDate
          ? getFormatedDateTime(new Date(eventEndDate), DateFormats.DateTime)
          : '-'}
      </Fragment>
    ),
    [eventEndDate, eventType, onlineSituation, situation]
  );

  const assemblyStartsIn = useMemo(
    () => (
      <Fragment>
        {eventType === 'Reunião'
          ? 'Sua reunião inicia em: '
          : situation === LegacyStatus.FINISHED ||
            [
              AssemblyStatus.ENCERRADO,
              AssemblyStatus.ENCERRADO_SEM_QUORUM,
            ].includes(onlineSituation)
          ? `Sua assembleia iniciou em: `
          : ` Sua assembleia inicia em: `}
        {eventDate
          ? getFormatedDateTime(new Date(eventDate), DateFormats.DateTime)
          : '-'}
      </Fragment>
    ),
    [eventDate, eventType, onlineSituation, situation]
  );

  const styleRule = useMemo(() => {
    if (
      [
        AssemblyModalities.OFFLINE,
        AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL,
      ].includes(modality)
    ) {
      if (
        [LegacyStatus.AVAILABLE_OR_BLOCKED].includes(situation) &&
        isDateLessThanToday(eventDate)
      ) {
        return styles.AVAILABLE;
      } else if (
        [AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(modality) &&
        onlineSituation === 2
      ) {
        return getStatusClassNameOnline(onlineSituation as AssemblyStatus);
      } else {
        return getStatusClassNamePresential(situation as LegacyStatus);
      }
    } else {
      return getStatusClassNameOnline(onlineSituation as AssemblyStatus);
    }
  }, [eventDate, modality, onlineSituation, situation]);

  return (
    <div
      className={clsx([styles.root, styleRule!])}
      onClick={() => {
        handleClick();
      }}
    >
      <header className={styles.header}>
        <div className={styles.header_text}>
          <strong>{coopName}</strong>

          <Tooltip
            title={title}
            placement='top'
            arrow
            disableHoverListener={title.length < 68}
          >
            <span>{title}</span>
          </Tooltip>
          <span>
            {eventId} - {eventType}
          </span>
        </div>

        <ChevronArrowRightIcon />
      </header>
      <div className={styles.body}>
        <span
          style={{
            marginTop: '-10px',
            fontSize: '12px',
            marginBottom: '12px',
            fontWeight: 'bold',
          }}
        >
          (Evento no horário de Brasília)
        </span>
        {[
          AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
          AssemblyModalities.HIBRIDO_SIMULTANEO,
          AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL,
        ].includes(modality!) ? (
          [
            AssemblyStatus.ENCERRADO,
            AssemblyStatus.ENCERRADO_SEM_QUORUM,
          ].includes(onlineSituation) ? (
            <Fragment>
              <div className={styles.content}>
                <p>
                  <strong>
                    {translation.Associado.Pages.home.Cards.EventoFinalizado}
                  </strong>
                  <br />
                </p>
                <strong>{assemblyStartsIn}</strong>
                <strong>{votingBy}</strong>
              </div>
              <Chip
                label={'Semi-Presencial'}
                icon={<LocalIcon />}
                iconPosition={'left'}
                styles={{
                  iconColor: '#3fa110',
                  background: '#D7E6C8',
                  borderColor: '#D7E6C8',
                  labelColor: '#323C32',
                }}
              />
            </Fragment>
          ) : (
            <Fragment>
              <div className={styles.content}>
                <h4>{translation.Associado.Pages.home.Cards.Digital}</h4>
                <p>{translation.Associado.Pages.home.Cards.ConfiraData}</p>
                <strong>{assemblyStartsIn}</strong>
                <strong>{votingBy}</strong>
              </div>
              <div className={styles.content}>
                <h4>{translation.Associado.Pages.home.Cards.Presencial}</h4>
                <div>
                  <Collapse opened={!openCollapse} animateOpacity>
                    <div className={styles.collapse}>
                      <p>
                        {translation.Associado.Pages.home.Cards.ConfiraLocal}
                        <AnimatedLink
                          animated
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenCollapse(true);
                          }}
                        >
                          {translation.Associado.Pages.home.Cards.VerMais}
                        </AnimatedLink>
                      </p>
                    </div>
                  </Collapse>
                  <Collapse opened={openCollapse} animateOpacity>
                    <div className={styles.collapse}>
                      {[AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA].includes(
                        modality!
                      ) ? (
                        <p>
                          {translation.Associado.Pages.home.Cards.VaParaAgencia}
                        </p>
                      ) : (
                        <p>
                          {address ? (
                            <div
                              style={{
                                display: 'flex',
                                marginBottom: '4px',
                                gap: '4px',
                              }}
                            >
                              <strong>Endereço:</strong>
                              <p>{address}</p>
                            </div>
                          ) : (
                            ''
                          )}
                          {neighborhood ? (
                            <div
                              style={{
                                display: 'flex',
                                marginBottom: '4px',
                                gap: '4px',
                              }}
                            >
                              <strong>Bairro:</strong>
                              <p>{neighborhood}</p>
                            </div>
                          ) : (
                            ''
                          )}
                          {cep ? (
                            <div
                              style={{
                                display: 'flex',
                                marginBottom: '4px',
                                gap: '4px',
                              }}
                            >
                              <strong>CEP:</strong>
                              <p>{formatCEP(cep)}</p>
                            </div>
                          ) : (
                            ''
                          )}
                          {city ? (
                            <div
                              style={{
                                display: 'flex',
                                marginBottom: '4px',
                                gap: '4px',
                              }}
                            >
                              <strong>Cidade:</strong>
                              <p>{city}</p>
                            </div>
                          ) : (
                            ''
                          )}
                          {state ? (
                            <div
                              style={{
                                display: 'flex',
                                marginBottom: '4px',
                                gap: '4px',
                              }}
                            >
                              <strong>Estado:</strong>
                              <p>{state}</p>
                            </div>
                          ) : (
                            ''
                          )}
                        </p>
                      )}

                      {![AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA].includes(
                        modality!
                      ) ? (
                        <p>
                          {translation.Associado.Pages.home.Cards.Observacao}
                          {
                            translation.Associado.Pages.home.Cards
                              .ConfirmePresencaGerente
                          }
                        </p>
                      ) : (
                        <Fragment />
                      )}
                      <strong>
                        <Fragment>
                          {eventType === 'Reunião'
                            ? 'Sua reunião inicia em: '
                            : 'Sua assembleia inicia em: '}
                          {eventDatePresential &&
                            getFormatedDateTime(
                              new Date(eventDatePresential),
                              DateFormats.DateTime
                            )}
                        </Fragment>
                      </strong>
                      <strong>
                        <Fragment>
                          {eventType === 'Reunião'
                            ? 'Término em: '
                            : 'Término em: '}
                          {eventEndDatePresential &&
                            getFormatedDateTime(
                              new Date(eventEndDatePresential),
                              DateFormats.DateTime
                            )}
                        </Fragment>
                      </strong>
                      <AnimatedLink
                        animated
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenCollapse(false);
                        }}
                      >
                        {translation.Associado.Pages.home.Cards.VerMenos}
                      </AnimatedLink>
                    </div>
                  </Collapse>
                </div>
                <Chip
                  label={'Semi-Presencial'}
                  icon={<LocalIcon />}
                  iconPosition={'left'}
                  styles={{
                    iconColor: '#3fa110',
                    background: '#D7E6C8',
                    borderColor: '#D7E6C8',
                    labelColor: '#323C32',
                  }}
                />
              </div>
            </Fragment>
          )
        ) : [AssemblyModalities.OFFLINE].includes(modality!) ? (
          <Fragment>
            <div className={styles.content}>
              <div style={{ marginBottom: '10px' }}>
                {address ? (
                  <div
                    style={{
                      display: 'flex',
                      marginBottom: '4px',
                      gap: '4px',
                    }}
                  >
                    <strong>Endereço:</strong>
                    <p>{address}</p>
                  </div>
                ) : (
                  ''
                )}
                {neighborhood ? (
                  <div
                    style={{
                      display: 'flex',
                      marginBottom: '4px',
                      gap: '4px',
                    }}
                  >
                    <strong>Bairro:</strong>
                    <p>{neighborhood}</p>
                  </div>
                ) : (
                  ''
                )}
                {cep ? (
                  <div
                    style={{
                      display: 'flex',
                      marginBottom: '4px',
                      gap: '4px',
                    }}
                  >
                    <strong>CEP:</strong>
                    <p>{formatCEP(cep)}</p>
                  </div>
                ) : (
                  ''
                )}
                {city ? (
                  <div
                    style={{
                      display: 'flex',
                      marginBottom: '4px',
                      gap: '4px',
                    }}
                  >
                    <strong>Cidade:</strong>
                    <p>{city}</p>
                  </div>
                ) : (
                  ''
                )}
                {state ? (
                  <div
                    style={{
                      display: 'flex',
                      marginBottom: '4px',
                      gap: '4px',
                      fontWeight: 'bold',
                    }}
                  >
                    <strong>Estado:</strong>
                    <p>{state}</p>
                  </div>
                ) : (
                  ''
                )}

                <p>
                  {translation.Associado.Pages.home.Cards.Observacao}
                  {situation === LegacyStatus.FINISHED ? (
                    <strong>
                      {translation.Associado.Pages.home.Cards.EventoFinalizado}
                    </strong>
                  ) : (
                    translation.Associado.Pages.home.Cards
                      .ConfirmePresencaAgencia
                  )}
                </p>
              </div>
              <strong>{assemblyStartsIn}</strong>
              <strong>{votingBy}</strong>
            </div>
            <Chip
              label={'Presencial'}
              icon={<LocalIcon />}
              iconPosition={'left'}
              styles={{
                iconColor: '#3fa110',
                background: '#D7E6C8',
                borderColor: '#D7E6C8',
                labelColor: '#323C32',
              }}
            />
          </Fragment>
        ) : (
          [AssemblyModalities.ONLINE].includes(modality!) && (
            <Fragment>
              <div className={styles.content}>
                <p>
                  {[
                    AssemblyStatus.ENCERRADO,
                    AssemblyStatus.ENCERRADO_SEM_QUORUM,
                  ].includes(onlineSituation) ? (
                    <strong>
                      {translation.Associado.Pages.home.Cards.EventoFinalizado}
                      <br />
                    </strong>
                  ) : [AssemblyStatus.PRE].includes(onlineSituation) ? (
                    <Fragment>
                      {translation.Associado.Pages.home.Cards.ConfiraData}
                      <br />
                    </Fragment>
                  ) : (
                    <Fragment>
                      {translation.Associado.Pages.home.Cards.CliqueAqui}
                      <br />
                    </Fragment>
                  )}
                </p>
                <strong>{assemblyStartsIn}</strong>
                <strong>{votingBy}</strong>
              </div>
              <Chip
                label={'Digital'}
                icon={<GlobeIcon />}
                iconPosition={'left'}
                styles={{
                  iconColor: '#3fa110',
                  background: '#D7E6C8',
                  borderColor: '#D7E6C8',
                  labelColor: '#323C32',
                }}
              />
            </Fragment>
          )
        )}
      </div>
    </div>
  );
}
